import { Box, Dialog } from '@mui/material'
import { formatRoute, SearchEmptyState, SearchInput, SearchList, SearchTitle, type SearchItem } from '@loop/ui'
import { capitalize, debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import searchPlaceholder from 'src/assets/icons/search-placeholder.svg'
import { useAuth } from 'src/context/AuthContext'
import { usePrivateLayoutDrawerContext } from 'src/context/PrivateLayoutDrawerContext'
import { useSearchContext } from 'src/context/SearchContext'
import { useSnackData } from 'src/context/SnackContext'
import { drawerCloseWidth, drawerWidth } from 'src/layout/components/PrivateLayoutDrawer'
import { AgentSearchService } from 'src/services/openApiV2'
import { sendSlackNotification } from 'src/utils/api'
import { SLACK_OAUTH_URL } from 'src/utils/config/config'
import { actionsMapping, ActionTypeEnum } from './utils/actions'

export type SearchType = {
  title: string
  path: string
}

export interface DefintionType {
  name: string
  definition: string
}

export interface ActionType {
  id: ActionTypeEnum
  title: string
  description: string
  command: string
  url: string
}

export interface FetchedRoutesType {
  category: string
  categoryKey: string
  route: string
  label: string
  routeIcon?: string | { label: string; value: string }
}

export interface SearchResult {
  corrected: string | null
  matched_tokens: string[]
  search: SearchType[]
  definitions: DefintionType[]
  actions: ActionType[]
}


const SearchDialog = ({ open, handleClose }: { open: boolean; handleClose: () => void }) => {
  const { categorizedRoutes, open: drawerOpen } = usePrivateLayoutDrawerContext()
  const { openError } = useSnackData()
  const navigate = useNavigate()
  const { currentUser, posthogCapture } = useAuth()
  const [, setSearchParams] = useSearchParams()
  const { setActionWizard, recentRoutes, recentRoutesLoading } = useSearchContext()

  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [searchLoading, setSearchLoading] = useState<boolean>(false)
  const [searchResults, setSearchResults] = useState<SearchResult>({} as SearchResult)

  const inputRef = useRef<HTMLInputElement>(null)

  const routes: FetchedRoutesType[] = useMemo(() => {
    const routeList: FetchedRoutesType[] = []

    Object.entries(categorizedRoutes).forEach(([, value]) => {
      value.routes.forEach((route) => {
        if (route.key) {
          if (route.subNav.length > 0) {
            route.subNav.forEach((subRoute) => {
              if (subRoute.key) {
                routeList.push({
                  category: value.category.name,
                  categoryKey: value.category.key,
                  route: subRoute.key.startsWith('/') ? formatRoute(subRoute.key) : formatRoute(`${route.key}/${subRoute.key}`),
                  routeIcon: route.icon,
                  label: route.label + ' - ' + subRoute.label
                })
              } else {
                routeList.push({
                  category: value.category.name,
                  categoryKey: value.category.key,
                  route: formatRoute(route.key),
                  routeIcon: route.icon,
                  label: route.label
                })
              }
            })
          } else {
            routeList.push({
              category: value.category.name,
              categoryKey: value.category.key,
              route: formatRoute(route.key),
              routeIcon: route.icon,
              label: route.label
            })
          }
        }
      })
    })

    routeList.push(
      { route: '/home', label: 'My Dashboard', category: 'Others', categoryKey: 'others', routeIcon: 'home' },
      { route: '/data-refresh-status', label: 'Data Refresh Status', category: 'Others', categoryKey: 'others', routeIcon: 'folder_copy' },
      { route: '/notification-settings', label: 'Notification Settings', category: 'Others', categoryKey: 'others', routeIcon: 'edit_notifications' },
      { route: '/user/profile', label: 'User Profile', category: 'Others', categoryKey: 'others', routeIcon: 'account_circle' },
      { route: '/user/management', label: 'User Management', category: 'Others', categoryKey: 'others', routeIcon: 'people_alt' }
    )

    return routeList
  }, [categorizedRoutes])

  const onClose = () => {
    handleClose()
    setTimeout(() => {
      setInputValue('')
      setSearch('')
    }, 300)
  }

  const handleNavigation = (path: string, resultIndex?: number) => {
    if (inputValue && inputValue.length >= 3 && resultIndex !== undefined) {
      posthogCapture('search_result_clicked', {
        query: inputValue,
        result_rank: resultIndex,
        result_path: path
      })
    }

    onClose()

    if (path === 'slack_oath') {
      window.open(SLACK_OAUTH_URL, '_blank')
      posthogCapture('slack_integration_button_clicked_search', {
        email: currentUser?.email || 'unknown'
      })
      sendSlackNotification({
        title: 'Slack Integration Button Clicked From Search',
        message: `Slack Integration Button Clicked From Search`,
        channel: 'fe-logs'
      })
    } else if (path.startsWith('/?')) {
      const params = path.slice(2)
      const paramsArray = params.split('&')
      const paramsObject = paramsArray.reduce(
        (acc, param) => {
          const [key, value] = param.split('=')
          acc[key] = value
          return acc
        },
        {} as Record<string, string>
      )

      setSearchParams((prev) => {
        Object.entries(paramsObject).forEach(([key, value]) => {
          prev.set(key, value)
        })
        return prev
      })
    } else {
      navigate(path)
    }
  }

  const handleActionWizard = (actionWizard: ActionType) => {
    onClose()

    if (actionsMapping[actionWizard.id]) {
      setActionWizard(actionWizard)
    } else {
      handleNavigation(actionWizard.url)
    }
  }

  const debouncedSetSearch = useMemo(
    () => debounce((searchValue: string) => {
      setSearch(searchValue)
    }, 300),
    []
  )

  const handleSearchChange = useCallback((value: string) => {
    setInputValue(value)
    debouncedSetSearch(value)
  }, [debouncedSetSearch])

  const searchRoutes = async (searchQuery: string) => {
    setSearchLoading(true)
    try {
      const res = (await AgentSearchService.searchApiSearchGet({ query: searchQuery })) as SearchResult

      const formattedResults = res.search.map((item) => {
        if (item?.path?.includes('?')) {
          const params = item?.path?.split('?')[1]
          const paramsArray = params?.split('&')

          let paramsString = ''

          paramsArray.forEach((param) => {
            const [, value] = param.split('=')
            const formattedValue = value
              .replace(/[-_]/g, ' ')
              .split(' ')
              .map((word) => capitalize(word))
              .join(' ')
            paramsString += `${formattedValue}`
          })

          return {
            ...item,
            title: `${item.title} (${paramsString})`
          }
        } else {
          return item
        }
      })

      setSearchResults({
        ...res,
        search: formattedResults
      })

      posthogCapture('search_query_executed', {
        query: searchQuery,
        has_results: res.search?.length > 0,
        top_result: res.search?.[0]?.title || 'not_found',
        result_count: res.search?.length || 0
      })
    } catch (err) {
      openError((err as Error).message)
    } finally {
      setSearchLoading(false)
    }
  }

  useEffect(() => {
    if (search && search.length >= 3) {
      searchRoutes(search)
    } else {
      setSearchResults({} as SearchResult)
    }
  }, [search])

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 0)

      return () => clearTimeout(timer)
    }
  }, [open])

  const mapToSearchItems = (searchItems: SearchType[]): SearchItem[] => {
    return searchItems
      .map((item, index) => {
        const routeCategory = routes.find((route) => formatRoute(item.path) === route.route)
        if (!routeCategory) return null

        const hasParams = item.path.includes('?')
        const icon = routeCategory?.routeIcon
          ? typeof routeCategory?.routeIcon === 'string'
            ? routeCategory?.routeIcon
            : routeCategory?.routeIcon?.value
          : 'block'

        return {
          id: `${item.path}-${index}`,
          title: hasParams ? (item.title?.split('-')?.[1] ?? item.title) : routeCategory?.label,
          path: item.path,
          icon,
          category: routeCategory?.category
        }
      })
      .filter((item): item is SearchItem => item !== null)
  }

  const handleItemSelect = (item: SearchItem, index: number) => {
    handleNavigation(item.path, index)
  }

  const currentDrawerWidth = drawerOpen ? drawerWidth : drawerCloseWidth
  const hasSearch = inputValue.length >= 3
  const searchItems = hasSearch ? mapToSearchItems(searchResults?.search || []) : []
  const recentItems = mapToSearchItems(recentRoutes?.slice(0, 4) || [])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: '10px',
          width: `calc(100% - ${currentDrawerWidth + 60}px)`,
          position: 'absolute',
          left: `${currentDrawerWidth}px`,
          top: '0px',
          maxWidth: '100%'
        }
      }}>
      <Box>
        <SearchInput
          ref={inputRef}
          value={inputValue}
          onChange={handleSearchChange}
          loading={searchLoading}
          placeholder="Search anything in Loop"
          keyboardHint="Esc to close"
        />

        <Box p={2} display="flex" flexDirection="column" gap={1}>
          {!hasSearch && (
            <Box>
              <SearchEmptyState title="Start typing to see results" imageSrc={searchPlaceholder} />

              {recentItems.length > 0 && (
                <Box mt={2}>
                  <SearchTitle title="Recently Visited Pages" loading={recentRoutesLoading} />
                  <SearchList items={recentItems} onItemSelect={handleItemSelect} loading={recentRoutesLoading} />
                </Box>
              )}
            </Box>
          )}

          {hasSearch && searchItems.length === 0 && !searchLoading && (
            <Box>
              <SearchEmptyState
                title={`No results found for "${inputValue}"`}
                description="Try using a different search term."
                imageSrc={searchPlaceholder}
              />

              {recentItems.length > 0 && (
                <Box mt={2}>
                  <SearchTitle title="Recently Visited Pages" loading={recentRoutesLoading} />
                  <SearchList items={recentItems} onItemSelect={handleItemSelect} loading={recentRoutesLoading} />
                </Box>
              )}
            </Box>
          )}

          {hasSearch && searchItems.length > 0 && (
            <Box>
              <SearchTitle title="Matching Pages" />
              <SearchList items={searchItems} onItemSelect={handleItemSelect} loading={searchLoading} />
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  )
}

export default SearchDialog
